import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Loader2, SendHorizontal } from 'lucide-react';

export interface ChatInputProps {
    input: string;
    setInput: (value: string) => void;
    onSubmit: () => void;
    isWaitingResponse: boolean;
    placeholder?: string;
}

export const ChatInput: React.FC<ChatInputProps> = ({ input, setInput, onSubmit, isWaitingResponse, placeholder }) => {
    const { t } = useTranslation();
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // Auto-resize textarea
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
        }
    }, [input]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (input.trim() && !isWaitingResponse) {
                onSubmit();
            }
        }
    };

    const handleSendClick = () => {
        if (input.trim() && !isWaitingResponse) {
            onSubmit();
        }
    };

    const defaultPlaceholder = t('agent.catalog.chat.input.placeholder', '메시지를 입력하세요...');

    return (
        <div className="mt-auto px-4 py-3 border-chatbot-line bg-card">
            <div className="flex items-end gap-3 mx-auto">
                <div className="flex-1 relative">
                    <textarea
                        ref={textareaRef}
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={placeholder || defaultPlaceholder}
                        className="w-full resize-none outline-none bg-transparent text-sm leading-relaxed pr-12 py-2 max-h-[120px] min-h-[44px] border border-chatbot-line rounded-lg px-3 focus:border-point transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        rows={2}
                        disabled={isWaitingResponse}
                    />
                    {/*<div className="absolute right-10 bottom-3 text-xs text-muted-foreground">*/}
                    {/*    {input.length}/2000*/}
                    {/*</div>*/}
                    <div className="absolute right-2 bottom-3">
                        <button
                            onClick={handleSendClick}
                            disabled={!input.trim() || isWaitingResponse}
                            className="flex-shrink-0 p-1 rounded hover:bg-box dark:hover:bg-hover transition-colors disabled:cursor-not-allowed"
                        >
                            {isWaitingResponse ? (
                                <Loader2 className="w-5 h-5 animate-spin text-white" />
                            ) : (
                                <SendHorizontal
                                    className="w-[18px] h-[18px]"
                                    fill={
                                        input.trim() && !isWaitingResponse
                                            ? 'hsl(var(--point))'
                                            : 'hsl(var(--text-500))'
                                    }
                                    stroke={
                                        input.trim() && !isWaitingResponse
                                            ? 'hsl(var(--point))'
                                            : 'hsl(var(--text-500))'
                                    }
                                />
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
